import styled, { css } from 'styled-components';
import Required from '../components/required';
import RadioGroup from '../components/radioGroup';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Button from '../components/button';
import Api from '../utils/api';
import Error from '../components/error';
import MaskedInput from 'react-text-mask/dist/reactTextMask';
import CustomTooltip from '../components/tooltip';
import { useSearchParams } from 'react-router';

const Details = ({ onSubmit, details = {} }) => {
    const [selectedOption, setSelectedOption] = useState('payment');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchParams] = useSearchParams();
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
    } = useForm();

    const id = searchParams.get('id');

    useEffect(() => {
        if (details) {
            Object.entries(details).forEach(([key, value]) => {
                if (value !== undefined) {
                    setValue(key, value);
                }
            });
        }
    }, [details, setValue]);

    const options = [
        { value: 'payment', label: 'Make a Payment' },
        {
            value: 'updateRecurring',
            label: 'Update Recurring Account Information',
        },
    ];

    const submit = useCallback(
        async (d) => {
            setLoading(true);
            try {
                if (!id) {
                    const req = await Api.post('/lookup', d);
                    d.mode = selectedOption;
                    onSubmit(d, req.data);
                } else {
                    d.mode = selectedOption;
                    onSubmit(d);
                }
            } catch (e) {
                if (e.response?.data && e.response.data.error) {
                    setError(e.response.data.error);
                } else {
                    setError('Unable to lookup EFA');
                }
            }
            setLoading(false);
        },
        [id, selectedOption]
    );

    return (
        <PageContainer onSubmit={handleSubmit(submit)}>
            <h2>Loan Details</h2>
            <Required>
                <span>i</span>All fields are required
            </Required>
            <RadioGroup options={options} value={selectedOption} onChange={setSelectedOption} />
            <FieldContainer>
                <Label>
                    EFA No.{' '}
                    <CustomTooltip
                        placement={'top'}
                        arrow
                        title={
                            <>
                                This is your <strong>Equipment Finance Agreement</strong> Number, you can find this on
                                your documentation or you may contact us at <strong>855-553-2735</strong> and we can
                                help you retrieve it.
                            </>
                        }
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_276_28023)">
                                <path
                                    d="M7.5763 7.49999C7.77222 6.94305 8.15893 6.47341 8.66793 6.17427C9.17693 5.87512 9.77538 5.76577 10.3573 5.86558C10.9392 5.96539 11.467 6.26793 11.8472 6.7196C12.2274 7.17127 12.4355 7.74293 12.4346 8.33332C12.4346 9.99999 9.93464 10.8333 9.93464 10.8333M10.0013 14.1667H10.0096M18.3346 9.99999C18.3346 14.6024 14.6037 18.3333 10.0013 18.3333C5.39893 18.3333 1.66797 14.6024 1.66797 9.99999C1.66797 5.39762 5.39893 1.66666 10.0013 1.66666C14.6037 1.66666 18.3346 5.39762 18.3346 9.99999Z"
                                    stroke="#00838F"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </g>
                            <defs>
                                <clipPath id="clip0_276_28023">
                                    <rect width="20" height="20" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    </CustomTooltip>
                </Label>
                <Input
                    type={'number'}
                    {...register('efa', {
                        valueAsNumber: true,
                        required: true,
                    })}
                    error={errors.efa}
                    disabled={!!id}
                />
            </FieldContainer>
            {error && (
                <Error>
                    <span>X</span> {error}
                </Error>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
                <Button variant={'contained'} type="submit" loading={loading} disabled={Object.keys(errors).length > 0}>
                    Next
                </Button>
            </div>
        </PageContainer>
    );
};

const CustomMaskedInput = forwardRef((props, ref) => {
    return (
        <MaskedInput
            {...props}
            ref={(inputRef) => {
                ref(inputRef ? inputRef.inputElement : null);
            }}
        />
    );
});

const Select = styled.select`
    padding: 10px;
    border-radius: 5px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 600;
    box-sizing: border-box;

    ${({ error }) =>
        error &&
        css`
            border: 2px solid #d90000;
            padding: 9px;
        `}
    &:focus {
        outline: none;
        border: 2px solid #4a154b;
        padding: 9px;
    }
`;

const Label = styled.div`
    color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
`;

const FieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const PageContainer = styled.form`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;

    h2 {
        margin: 0;
    }
`;

const Input = styled.input`
    padding: 10px;
    border-radius: 5px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 600;
    box-sizing: border-box;

    ${({ error }) =>
        error &&
        css`
            border: 2px solid #d90000;
            padding: 9px;
        `}
    &:focus {
        outline: none;
        border: 2px solid #4a154b;
        padding: 9px;
    }
`;

const DualField = styled.div`
    display: flex;
    gap: 10px;

    & ${FieldContainer} {
        flex: 1;
    }

    @media (max-width: 600px) {
        flex-direction: column;
    }
`;

const Content = styled.div``;

const CheckboxContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;

    input[type='checkbox'] {
        width: 18px;
        height: 18px;
        cursor: pointer;
    }

    label {
        cursor: pointer;
        color: rgba(0, 0, 0, 0.7);
    }
`;

export default Details;
